import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Pause, Download, ShoppingCart, Layers, ChevronLeft, ChevronRight } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { coverUrl, downloadTrackUrl, fmtDuration, queueAfter, trackSrc, trackVersionsPlayable, buyItemFor, detailPathFor, sectionOf, SECTION_LABELS } from "@/lib/api";
import { VersionActions } from "@/components/VersionActions";
import BizumModal from "@/components/BizumModal";
import { Waveform } from "@/components/Waveform";
import { TrackStats } from "@/components/TrackStats";
import { StarRating } from "@/components/StarRating";
import { Pager, usePager } from "@/components/Pager";
import { useAccessCode } from "@/context/AccessCodeContext";
import { useAuth } from "@/context/AuthContext";
import { ShareButton, shareUrlFor, storyUrlFor } from "@/components/ShareButton";

export function TrackRow({ track, queue = [], index, config }) {
  const p = usePlayer();
  const navigate = useNavigate();
  const ac = useAccessCode();
  const auth = useAuth();
  const [bizumOpen, setBizumOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isCur = p?.current?.id === track.id || p?.current?.track_id === track.id;
  const showWave = expanded || isCur;
  const playing = isCur && p?.isPlaying;
  const play = (e) => { e?.stopPropagation(); if (isCur) return p.togglePlay(); p.playTrack(track, queueAfter(queue, track.id)); };

  return (
    <>
      <div data-testid={`track-row-${track.id}`} onClick={play}
        className={`relative flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 rounded-xl border cursor-pointer select-none transition-colors card-glow ${isCur ? "bg-red-950/20 border-red-900/40" : "bg-[#16161D] border-white/5 hover:bg-white/[0.03]"}`}>
        <div className="relative w-24 h-24 sm:w-36 sm:h-36 rounded-lg overflow-hidden bg-neutral-900 shrink-0">
          {track.cover_path && <img src={coverUrl(track.cover_path)} alt="" className="w-full h-full object-cover" />}
          <button onClick={play} data-testid={`track-row-play-${track.id}`}
            className={`absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity ${isCur ? "opacity-100" : "opacity-0 hover:opacity-100"}`}>
            {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
          </button>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-0.5" data-testid={`track-row-corner-${track.id}`}>
          <ShareButton url={shareUrlFor(track)} storyUrl={storyUrlFor(track)} title={track.title} testid={`track-row-share-${track.id}`} className="p-2" />
          {track.price === 0 || ac.allows(track.is_pista ? "pack" : "track", track.is_pista ? track.pack_id : track.id) ? (
            <a href={ac.withCode(track.download_url || downloadTrackUrl(track.id))} onClick={(e) => { e.stopPropagation(); auth.requireAuth(e); }} data-testid={`track-row-download-${track.id}`} title={track.price === 0 ? "Descargar" : "Descargar con tu código"}
              className="p-2 rounded-lg text-white/60 hover:text-red-500 hover:bg-red-600/10 shrink-0"><Download className="w-4 h-4" /></a>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); setBizumOpen(true); }} data-testid={`track-row-buy-${track.id}`} title="Comprar con PayPal"
              className="p-2 rounded-lg text-red-400 hover:text-white hover:bg-red-600/20 shrink-0"><ShoppingCart className="w-4 h-4" /></button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <button type="button" onClick={(e) => { e.stopPropagation(); navigate(detailPathFor(track)); }} data-testid={`track-row-title-${track.id}`}
            className={`block w-full text-left pr-16 font-display font-bold text-sm sm:text-lg uppercase tracking-tight leading-snug break-words hover:text-red-500 ${isCur ? "text-red-400" : "text-white"}`}>{track.title}</button>
          <div className="text-sm text-white flex flex-wrap items-center gap-2 mt-1">
            {track.artist}
            {(track.versions || []).length > 0 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }} data-testid={`track-row-versions-${track.id}`}
                className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300 font-semibold"><Layers className="w-3 h-3" />{track.versions.length + 1} versiones</button>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-2 h-6">
            {!track.is_pista && <StarRating track={track} className="mr-2" />}
            {track.bpm && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-sky-400 border-sky-800/50 bg-sky-950/40">{track.bpm} BPM</span>}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 mt-3 flex-wrap" data-testid={`track-row-actions-${track.id}`}>
          {config?.show_stats && <TrackStats track={track} />}
          <span className="font-mono text-xs text-white/40 shrink-0 mr-1">{fmtDuration(track.duration)}</span>
          <span data-testid={`track-price-badge-${track.id}`}
            className={`shrink-0 hidden sm:flex items-center px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full border ${
              track.price === 0 ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-600/25 text-red-400 border-red-600/40"
            }`}>{track.price === 0 ? "Free" : `€${track.price.toFixed(2)}`}</span>
          <Link to={`/${{ remix: "remixes", mashup: "mashups", original: "pool", pool: "pool" }[sectionOf(track)]}`} onClick={(e) => e.stopPropagation()} data-testid={`track-row-section-${track.id}`} title="Ir a la sección"
            className="shrink-0 hidden xl:flex items-center px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full border text-sky-400 border-sky-800/60 bg-sky-950/40 hover:bg-sky-900/50 hover:text-white transition-colors">{SECTION_LABELS[sectionOf(track)]}</Link>
          {track.pack_id && (
            <Link to={`/pack/${track.pack_id}`} onClick={(e) => e.stopPropagation()} data-testid={`track-row-pack-link-${track.id}`} title={track.pack_title ? `Ver pack: ${track.pack_title}` : "Ver pack"}
              className="shrink-0 flex items-center px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full border text-sky-400 border-sky-800/60 bg-sky-950/40 hover:bg-sky-900/50 hover:text-white transition-colors">Pack</Link>
          )}
          <Link to={detailPathFor(track)} onClick={(e) => e.stopPropagation()} data-testid={`track-row-view-${track.id}`} title="Ver tema"
            className="shrink-0 flex items-center px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full border text-emerald-400 border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/50 hover:text-white transition-colors">Ver tema</Link>
          </div>
          {showWave && (track.versions || []).length > 0 && (
            <div className="mt-3 mr-2 divide-y divide-white/5 border border-white/5 rounded-xl overflow-hidden bg-black/30" onClick={(e) => e.stopPropagation()} data-testid={`track-row-versions-list-${track.id}`}>
              {trackVersionsPlayable(track).map((v) => {
                const vCur = p?.current?.id === v.id;
                const all = trackVersionsPlayable(track);
                return (
                  <div key={v.id} data-testid={`track-version-row-${v.id}`} onClick={() => p.playTrack(v, queueAfter(all, v.id))}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer rounded-lg first:rounded-t-lg last:rounded-b-lg ${vCur ? "bg-red-950/20" : "hover:bg-white/[0.03]"}`}>
                    <button onClick={(e) => { e.stopPropagation(); p.playTrack(v, queueAfter(all, v.id)); }} data-testid={`track-version-play-${v.id}`}
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${vCur ? "btn-red text-white" : "bg-white/5 hover:bg-red-600/30 text-white"}`}>
                      {vCur && p?.isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
                    </button>
                    <span className={`text-xs font-semibold truncate flex-1 ${vCur ? "text-red-400" : "text-white/90"}`}>{v.version_title}</span>
                    <span className="font-mono text-[10px] text-white/40 w-9 text-right">{fmtDuration(v.duration)}</span>
                    <VersionActions track={track} version={v} config={config} compact />
                  </div>
                );
              })}
            </div>
          )}
          {showWave && (
            <div className="mt-3 h-12 pr-2" onClick={(e) => e.stopPropagation()} data-testid={`track-row-waveform-${track.id}`}>
              <Waveform src={trackSrc(track)} progress={isCur ? p.progress : 0} duration={isCur ? p.duration : track.duration || 0}
                previewSec={null} previewStart={Number(track.preview_start) || 0}
                onSeek={(t) => { if (isCur) return p.seek(t); p.playTrack(track, queueAfter(queue, track.id)); setTimeout(() => p.seek(t), 900); }} />
            </div>
          )}
        </div>
      </div>
      <BizumModal open={bizumOpen} onClose={() => setBizumOpen(false)} {...buyItemFor(track)} config={config} />
    </>
  );
}

export function TrackList({ tracks, config, testid, perPage = 10, sideNav = false, grid = false }) {
  const pg = usePager(tracks, perPage);
  const rows = pg.slice.map((t, i) => <TrackRow key={t.id} track={t} index={i} queue={tracks} config={config} />);
  if (grid) {
    return (
      <div data-testid={testid}>
        <div className="grid lg:grid-cols-2 gap-4">{rows}</div>
        <Pager {...pg} testid={`${testid || "track-list"}-pager`} />
      </div>
    );
  }
  if (!sideNav) {
    return (
      <div className="space-y-3" data-testid={testid}>
        {rows}
        <Pager {...pg} testid={`${testid || "track-list"}-pager`} />
      </div>
    );
  }
  const tab = "flex-1 h-10 rounded-xl border flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-20 border-white/10 text-white/60 hover:text-white hover:border-red-600/60 hover:bg-red-600/10";
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows}
      {pg.pages > 1 && (
        <div className="flex items-stretch gap-2" data-testid={`${testid}-sidenav`}>
          <button type="button" onClick={() => pg.setPage(pg.page - 1)} disabled={pg.page <= 1} className={tab} data-testid={`${testid}-prev`} title="Anteriores">
            <ChevronLeft className="w-4 h-4" /><span>{pg.page > 1 ? `Anteriores · ${pg.page - 1} / ${pg.pages}` : "Anteriores"}</span>
          </button>
          <button type="button" onClick={() => pg.setPage(pg.page + 1)} disabled={pg.page >= pg.pages} className={tab} data-testid={`${testid}-next`} title="Siguientes">
            <span>{pg.page < pg.pages ? `Siguientes · ${pg.page + 1} / ${pg.pages}` : `${pg.page} / ${pg.pages}`}</span><ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
